import { useState } from 'react';
import { ColorField, Hint, Panel, Segmented, Slider, Toggle } from './ui/Controls';
import { store, useStore } from '@/state/store';
import { FONT_STACK, HN_WEIGHTS } from '@/lib/fonts';
import { safeBalance } from '@/engine/typography/textOptimizer';
import type { Align, TextTransform } from '@/types';

/**
 * Per-block typography. Weight, size, spacing and case are all editable; the
 * font family is deliberately behind a lock, because the whole design system
 * assumes Helvetica Neue metrics. Unlocking is possible but explicit.
 */
export function TypographySelector() {
  const spec = useStore((s) => s.variants.find((v) => v.id === s.activeId) ?? null);
  const selectedId = useStore((s) => s.selectedBlockId);
  const [familyUnlocked, setFamilyUnlocked] = useState(false);

  const block =
    spec?.blocks.find((b) => b.id === selectedId) ??
    spec?.blocks.find((b) => b.role === 'headline') ??
    null;

  if (!spec || !block) {
    return (
      <Panel title="Typography">
        <p className="text-[11px] text-ink-500">
          Generate a design, then click any text on the canvas to edit it.
        </p>
      </Panel>
    );
  }

  const update = (patch: Partial<typeof block>, key?: string) => {
    store.updateActive(
      (current) => ({
        ...current,
        blocks: current.blocks.map((b) => (b.id === block.id ? { ...b, ...patch } : b)),
      }),
      key,
    );
  };

  const rebreak = (maxLines: number) => {
    const lines = safeBalance(block.text, {
      maxLines,
      targetEmWidth: block.width / block.fontSize,
      tracking: block.tracking,
    });
    update({ lines, autoLines: true });
  };

  return (
    <Panel
      title="Typography"
      action={
        <span className="text-[10px] capitalize text-ink-500">{block.role}</span>
      }
    >
      <div className="space-y-2.5">
        <div>
          <span className="field-label">Weight — Helvetica Neue</span>
          <div className="grid grid-cols-3 gap-1">
            {HN_WEIGHTS.map((w) => (
              <button
                key={w.value}
                type="button"
                onClick={() => update({ weight: w.value })}
                style={{ fontFamily: FONT_STACK, fontWeight: w.value }}
                className={`rounded-md px-1 py-1.5 text-[10px] transition ${
                  block.weight === w.value
                    ? 'bg-accent text-ink-950'
                    : 'bg-white/[0.03] text-ink-300 hover:bg-white/[0.07]'
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        <Slider
          label="Size"
          min={0.012}
          max={0.24}
          step={0.001}
          value={block.fontSize}
          onChange={(v) => update({ fontSize: v, autoLines: false }, `size-${block.id}`)}
          format={(v) => `${(v * 1080).toFixed(0)}px @1080`}
        />

        <Slider
          label="Letter spacing"
          min={-0.06}
          max={0.5}
          step={0.002}
          value={block.tracking}
          onChange={(v) => update({ tracking: v }, `track-${block.id}`)}
          format={(v) => `${v >= 0 ? '+' : ''}${(v * 1000).toFixed(0)}`}
        />

        <Slider
          label="Line height"
          min={0.8}
          max={2.2}
          step={0.01}
          value={block.lineHeight}
          onChange={(v) => update({ lineHeight: v }, `lead-${block.id}`)}
          format={(v) => `${v.toFixed(2)}×`}
        />

        <Slider
          label="Column width"
          min={0.2}
          max={0.96}
          step={0.005}
          value={block.width}
          onChange={(v) => update({ width: v }, `width-${block.id}`)}
          format={(v) => `${(v * 100).toFixed(0)}%`}
        />

        <div>
          <span className="field-label">Alignment</span>
          <Segmented<Align>
            options={[
              { value: 'left', label: 'Left' },
              { value: 'center', label: 'Center' },
              { value: 'right', label: 'Right' },
            ]}
            value={block.align}
            onChange={(v) => update({ align: v })}
          />
        </div>

        <div>
          <span className="field-label">Case</span>
          <Segmented<TextTransform>
            options={[
              { value: 'none', label: 'As typed' },
              { value: 'upper', label: 'CAPS' },
              { value: 'lower', label: 'lower' },
            ]}
            value={block.transform}
            onChange={(v) => update({ transform: v })}
          />
        </div>

        <ColorField
          label="Text color"
          value={block.color}
          onChange={(v) => update({ color: v })}
        />

        <Slider
          label="Opacity"
          min={0.1}
          max={1}
          value={block.opacity}
          onChange={(v) => update({ opacity: v }, `opacity-${block.id}`)}
          format={(v) => `${(v * 100).toFixed(0)}%`}
        />

        <div className="border-t border-white/[0.06] pt-2.5">
          <Toggle
            label="Text shadow"
            checked={Boolean(block.shadow)}
            onChange={(on) =>
              update({
                shadow: on
                  ? { blur: 0.014, x: 0, y: 0.002, color: '#000000', alpha: 0.5 }
                  : null,
              })
            }
          />
          {block.shadow && (
            <div className="mt-1.5 space-y-2 pl-1">
              <Slider
                label="Shadow blur"
                min={0}
                max={0.06}
                step={0.001}
                value={block.shadow.blur}
                onChange={(v) =>
                  update({ shadow: { ...block.shadow!, blur: v } }, `sb-${block.id}`)
                }
                format={(v) => `${(v * 1080).toFixed(0)}px`}
              />
              <Slider
                label="Shadow strength"
                min={0}
                max={1}
                value={block.shadow.alpha}
                onChange={(v) =>
                  update({ shadow: { ...block.shadow!, alpha: v } }, `sa-${block.id}`)
                }
                format={(v) => `${(v * 100).toFixed(0)}%`}
              />
            </div>
          )}
        </div>

        <div className="border-t border-white/[0.06] pt-2.5">
          <span className="field-label">Line breaks</span>
          <div className="flex gap-1">
            {[1, 2, 3, 4].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => rebreak(n)}
                className={`flex-1 rounded-md py-1.5 text-[10px] transition ${
                  block.lines.length === n
                    ? 'bg-white/[0.1] text-ink-100'
                    : 'bg-white/[0.03] text-ink-400 hover:bg-white/[0.07]'
                }`}
              >
                {n} line{n > 1 ? 's' : ''}
              </button>
            ))}
          </div>
          <div className="mt-1.5 space-y-1">
            {block.lines.map((line, i) => (
              <input
                key={`${block.id}-line-${i}`}
                value={line}
                onChange={(e) => {
                  const lines = block.lines.slice();
                  lines[i] = e.target.value;
                  update({ lines, autoLines: false });
                }}
                className="text-input px-2 py-1 text-[11px]"
                aria-label={`Line ${i + 1}`}
              />
            ))}
          </div>
          <Hint>
            Editing a line here changes the words on the canvas. The automatic
            balancer only ever moves breaks.
          </Hint>
        </div>

        <div className="border-t border-white/[0.06] pt-2.5">
          <Toggle
            label="Override font family"
            checked={familyUnlocked}
            onChange={(on) => {
              setFamilyUnlocked(on);
              if (!on) update({ fontFamily: '' });
            }}
            hint="Every style is designed around Helvetica Neue metrics."
          />
          {familyUnlocked && (
            <input
              value={block.fontFamily}
              onChange={(e) => update({ fontFamily: e.target.value })}
              placeholder='e.g. "Helvetica Neue", Helvetica, sans-serif'
              className="text-input mt-1.5 font-mono text-[10px]"
            />
          )}
        </div>
      </div>
    </Panel>
  );
}
